import * as React from 'react';
import { appStore } from '@app/Stores/AppStore';
import { permissionStore } from '@app/Stores/PermissionStore';

export interface IWindowAdal extends Window {
	adal?: {
		redirectToLogin(): void;
		redirectToLogout(): void;
	};
}

export class UserRoles {
    public static readonly administrator: string = 'DataSync.Administrator';
    public static readonly support: string = 'DataSync.Support';
    public static readonly flightsEditing: string = 'DataSync.Flights.Editing';
}

// Value = bit position; must match the backend PermissionType enum / sec.Permission.Id.
export enum UserPermissions {
    GeneralAdminWrite = 1,
    GeneralAdminRead = 2,
    AdminToolsSafe = 3,
    FullPermissionImpersonate = 5,

    StationRead = 10,
    StationGeneralWrite = 11,
    StationPantryRead = 12,
    StationPantryWrite = 13,
    StationWaterQuantityRead = 14,
    StationWaterQuantityWrite = 15,
    StationHotelManagementRead = 16,
    StationHotelManagementWrite = 17,

    AircraftRead = 20,
    AircraftGeneralWrite = 21,
    AircraftAddAircraft = 22,
    AircraftMassCorrectionRead = 23,
    AircraftMassCorrectionWrite = 24,
    AircraftWeightRead = 25,
    AircraftWeightWrite = 26,

    FlightRead = 30,
    FlightWrite = 31,
    FlightDetailsRead = 32,
    FlightSummariesRead = 33,
    FlightSummariesWrite = 34,
    FlightCrewRead = 35,
    FlightWeightRead = 36,
    FlightWeightWrite = 37,
    FlightCreate = 38
}

export class AzureAuth extends React.Component<{}, {}> {

    static logOut() {
        appStore.clearUserData();
		const appWindow: IWindowAdal = window;
		appWindow.adal && appWindow.adal.redirectToLogout();
    }

    static login() {
        appStore.clearUserData();
		const appWindow: IWindowAdal = window;
		appWindow.adal && appWindow.adal.redirectToLogin();
    }

    static get isDcc() {
		const appWindow: IWindowAdal = window;
        return appWindow.appTenant.code.toLowerCase() === 'dcc';
    }

    static get isDataBridge() {
		const appWindow: IWindowAdal = window;
        return appWindow.appTenant.code.toLowerCase() === 'databridge';
    }

    // GeneralAdminWrite is the superuser permission and satisfies every check.
    static hasAccess(permissions: number | number[]) {
        const hasAdmin = permissionStore.currentUserPermissions.includes(UserPermissions.GeneralAdminWrite);
        if (hasAdmin) return true;

        if (typeof permissions === 'number') {
            return permissionStore.currentUserPermissions.indexOf(permissions) !== -1;
        }

        if (Array.isArray(permissions)) {
            for (const permission of permissions) {
                if (permissionStore.currentUserPermissions.indexOf(permission) !== -1) {
                    return true;
                }
            }
        }

        return false;
    }

    static get hasPermissions() {
        return permissionStore.currentUserPermissions.length > 0;
    }

    static get canImpersonatePermissions() {
        return permissionStore.realUserPermissions.includes(UserPermissions.GeneralAdminWrite)
            || permissionStore.realUserPermissions.includes(UserPermissions.FullPermissionImpersonate);
    }

    static get isImpersonatingPermissions() {
        return permissionStore.isImpersonatingPermissions;
    }

    static get permissionsEnabled() {
        return permissionStore.permissionsEnabled;
    }

    static get permissionsFlagLoaded() {
        return permissionStore.permissionsFlagLoaded;
    }

    static get permissionsLoaded() {
        return permissionStore.permissionsLoaded;
    }

    static get canSeeGeneralAdmin() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.GeneralAdminRead, UserPermissions.GeneralAdminWrite])
            : this.isUserInAdminGroup;
    }

    static get canWriteGeneralAdmin() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.GeneralAdminWrite)
            : this.isUserInAdminGroup;
    }

    static get canRunAdminTools() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.AdminToolsSafe, UserPermissions.GeneralAdminWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeStationPantry() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationPantryRead)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }
    static get canWriteStationPantry() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationPantryWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeWaterQuantity() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationWaterQuantityRead)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }
    static get canWriteWaterQuantity() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationWaterQuantityWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeHotelManagement() {
        // Without the permission system the hotel-details API stays open to every authenticated user.
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationHotelManagementRead)
            : true;
    }
    static get canWriteHotelManagement() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.StationHotelManagementWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canWriteAircrafts() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.AircraftGeneralWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canAddAircraft() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.AircraftAddAircraft)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeStations() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.StationRead, UserPermissions.StationGeneralWrite])
            : this.isUserInAdminGroup;
    }

    static get canSeePantry() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.StationPantryRead, UserPermissions.StationPantryWrite])
            : this.isUserInAdminGroup;
    }

    static get canSeeAircrafts() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.AircraftRead, UserPermissions.AircraftGeneralWrite])
            : this.isUserInAdminGroup;
    }

    static get canSeeAircraftWeightCorrection() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.AircraftMassCorrectionRead, UserPermissions.AircraftMassCorrectionWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canWriteAircraftWeightCorrection() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.AircraftMassCorrectionWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeAircraftWeight() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.AircraftWeightRead)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }
    static get canWriteAircraftWeight() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.AircraftWeightWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeFlights() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.FlightRead, UserPermissions.FlightWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    static get canEditFlights(){
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    // Also gates editing and cancelling manual flights; the whole feature is off unless Feature.EnableManualFlights is true.
    static get canCreateFlights() {
        if (!permissionStore.manualFlightsEnabled) return false;

        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightCreate)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeFlightWeight() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.FlightWeightRead, UserPermissions.FlightWeightWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }
    static get canWriteFlightWeight() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightWeightWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeFlightDetails() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightDetailsRead)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    static get canSeeFlightSummaries() {
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.FlightSummariesRead, UserPermissions.FlightSummariesWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    static get canWriteFlightSummaries() {
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightSummariesWrite)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    static get canSeeFlightsNeedingAttention() {
        return permissionStore.flightsNeedingAttentionEnabled && this.canWriteFlightSummaries;
    }

    static get canSeeFlightCrew(){
        return this.permissionsEnabled
            ? this.hasAccess(UserPermissions.FlightCrewRead)
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    static get canSeeAdminTools(){
        return this.permissionsEnabled
            ? this.hasAccess([UserPermissions.GeneralAdminRead, UserPermissions.GeneralAdminWrite])
            : (this.isUserInAdminGroup || this.isUserInSupportGroup);
    }

    // Only DCC has a landing page at '/' (the job list); every other tenant starts on the Gantt.
    static get defaultRoute(): string {
        if (!this.isDcc) {
            if (this.canSeeFlights) return '/flight-schedule/gantt';
            if (this.canSeeStations) return '/stations';
            if (this.canSeeAircrafts) return '/aircrafts';
            return '/403';
        }

        if (this.canSeeGeneralAdmin) return '/';
        if (this.canSeeStations) return '/stations';
        if (this.canSeeAircrafts) return '/aircrafts';
        if (this.canSeeFlights) return '/flight-schedule/gantt';
        return '/403';
    }

    static get hasRole(){
        return this.permissionsEnabled
            ? this.hasPermissions
            : (this.isUserInAdminGroup || this.isUserInSupportGroup || this.isUserInFlightsEditing);
    }

    static get isUserInAdminGroup() {
        const user = appStore.currentUser;
        return !!(user && user.roles && user.roles.indexOf(UserRoles.administrator) > -1);
    }

    static get isUserInSupportGroup() {
        const user = appStore.currentUser;
        return !!(user && user.roles && user.roles.indexOf(UserRoles.support) > -1);
    }

    static get isUserInFlightsEditing() {
        const user = appStore.currentUser;
        return !!(user && user.roles && user.roles.indexOf(UserRoles.flightsEditing) > -1);
    }
}
